import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_AUDIT_LOGS,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_FALLBACK_LIMIT,
	LOOKUP_LIMIT,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {
	normalizeSearchFields,
	resolveByFields,
} from '../../core/fieldSearch';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return an audit log row identifier.
 * @param {object} item Canvas audit log row
 * @return {*} identifier
 */
function logId(item) {
	return item?.id ?? item?.pk ?? item?.log_id;
}

/**
 * Display name for an audit log row.
 * @param {object} item Canvas audit log row
 * @return {string} name
 */
function logName(item) {
	return optionalText(item?.username) ||
		optionalText(item?.description) ||
		optionalText(item?.email) ||
		optionalText(item?.model) ||
		(logId(item) == null ? '' : String(logId(item)));
}

/**
 * Map a stored field value to its catalogue label.
 * @param {object} field catalogue field
 * @param {*} stored stored field value
 * @return {string|null} label
 */
function labelFromChoices(field, stored) {
	if (stored == null || stored === '') return null;
	const text = String(stored).trim();
	const folded = text.toLocaleLowerCase();
	for (const choice of field.choices || []) {
		const names = [
			choice.id,
			choice.label,
			...(choice.values || []),
			...(choice.aliases || []),
		];
		if (names.some((name) => String(name).toLocaleLowerCase() === folded)) {
			return choice.label;
		}
	}
	return text;
}

/**
 * Copy a Canvas row with catalogued fields written as the UI names them.
 * @param {object} item Canvas audit log row
 * @param {*} searchFields field catalogue sent with the lookup
 * @return {object} row the generic lookup can read
 */
function rowForSearch(item, searchFields) {
	const fields = normalizeSearchFields(searchFields);
	const next = {...item};
	fields.forEach((field) => {
		for (const attribute of field.attributes) {
			if (!Object.prototype.hasOwnProperty.call(item, attribute)) continue;
			const labelled = labelFromChoices(field, item[attribute]);
			if (labelled != null) {
				next[attribute] = labelled;
				return;
			}
		}
	});
	return next;
}

/**
 * Normalize a Canvas audit log row.
 * @param {object} item Canvas row
 * @return {object|null} agent candidate
 */
function normalizeLog(item) {
	const id = logId(item);
	const name = logName(item);
	if (id == null || !name) return null;
	return {
		entity_type: 'audit_log',
		entity_id: String(id),
		name: String(name),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Load audit log rows, paging until the lookup cap.
 * @param {string|null} search search text
 * @param {number} limit maximum rows
 * @return {Promise<Array>} rows
 */
async function loadRows(search, limit) {
	const rows = [];
	const pageSize = Math.min(Math.max(limit, 1), LOOKUP_LIMIT);
	let offset = 0;
	while (rows.length < limit) {
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
		const url = `${ENDPOINT_URL}${GET_AUDIT_LOGS}` +
			`?deviceType=wifi&limit=${pageSize}&offset=${offset}${searchParam}`;
		const {data} = await axios.get(url);
		const page = responseRows(decodeAgentResponse(data) || {});
		const list = Array.isArray(page) ? page : [];
		rows.push(...list);
		if (list.length < pageSize) break;
		offset += pageSize;
	}
	return rows.slice(0, limit);
}

/**
 * Rank loaded rows through the catalogue the agent sent.
 * @param {Array} rows Canvas rows
 * @param {object} params resolver parameters
 * @param {string} query lookup query
 * @param {number} limit maximum candidates
 * @param {boolean} [fuzzy] allow near-miss matches
 * @return {Array} ranked candidates
 */
function candidatesFromRows(rows, params, query, limit, fuzzy = false) {
	return resolveByFields({
		rows: rows.map((row) => rowForSearch(row, params.search_fields)),
		normalizeRow: normalizeLog,
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
		fallbackValues: (candidate) => [candidate?.name],
		fuzzy,
	});
}

/**
 * Resolve Audit Logs candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveAuditLogs(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('audit_log', rows, logId);
		return {
			candidates: candidatesFromRows(rows, params, '', limit).slice(0, limit),
		};
	}
	let rows = await loadRows(query, LOOKUP_LIMIT);
	let candidates = candidatesFromRows(
		rows, params, query, LOOKUP_LIMIT,
	);
	if (!candidates.length && rows.length) {
		candidates = candidatesFromRows(rows, params, '', LOOKUP_LIMIT);
	}
	if (!candidates.length) {
		rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		candidates = candidatesFromRows(
			rows, params, query, LOOKUP_LIMIT,
		);
		if (!candidates.length && params.fuzzy === true) {
			candidates = candidatesFromRows(
				rows, params, query, LOOKUP_LIMIT, true,
			);
		}
	}
	replaceResolvedEntities('audit_log', rows, logId);
	return {candidates};
}

/**
 * Return a retained audit log row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedAuditLog(entityId) {
	return getResolvedEntity('audit_log', entityId);
}
